import { useEffect, useMemo, useRef, useState } from 'react';
import { LuCheck, LuChevronDown } from 'react-icons/lu';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import TextField from '@mui/material/TextField';
import './style.css';

const initialValues = {
  name: '',
  email: '',
  company: '',
  service: '',
  description: '',
  link: '',
};

const fieldLabels = {
  name: 'Имя *',
  email: 'Email *',
  company: 'Компания (необязательно)',
  service: 'Необходимая услуга *',
  description: 'Описание проекта *',
  link: 'Ссылка на файлы проекта (необязательно)',
};

const requestCopy = {
  formHeading: 'Расскажите о вашем проекте',
  intro: 'Укажите основные данные — это ускорит предварительную оценку проекта.',
  submit: 'Получить оценку проекта',
  loading: 'Отправляем запрос...',
  retry: 'Попробовать снова',
  note: 'Ответим в течение одного рабочего дня.',
  successHeading: 'Запрос получен',
  successText: 'Спасибо! Мы получили информацию о вашем проекте. Изучим материалы и свяжемся с вами в течение одного рабочего дня.',
  nextHeading: 'Что дальше?',
  nextSteps: [
    'Проверим предоставленные данные.',
    'При необходимости уточним детали.',
    'Подготовим оценку объёма работ, сроков и стоимости.',
  ],
  addHeading: 'Нужно что-то добавить?',
  addText: 'Напишите нам на',
  sendAnother: 'Отправить ещё один запрос',
  errorHeading: 'Не удалось отправить запрос',
  errorText: 'Данные остались в форме. Попробуйте ещё раз или напишите нам напрямую на',
  validation: {
    name: 'Укажите ваше имя.',
    email: 'Введите корректный email.',
    service: 'Выберите необходимую услугу.',
    description: 'Кратко опишите ваш проект.',
  },
};

const contactEmail = 'contact@dotpolyline.com';

const serviceOptions = [
  { value: '2d-cad', label: '2D CAD Drawings' },
  { value: '3d-bim', label: '3D / BIM Model' },
  { value: 'as-built', label: 'As-Built Documentation' },
  { value: 'other', label: 'Other' },
];

const validateField = (field, rawValue) => {
  const value = rawValue.trim();

  switch (field) {
    case 'name':
      if (!value) return requestCopy.validation.name;
      return '';

    case 'email':
      if (!value) return requestCopy.validation.email;
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
        return requestCopy.validation.email;
      }
      return '';

    case 'company':
      return '';

    case 'service':
      if (!value) return requestCopy.validation.service;
      return '';

    case 'description':
      if (!value) return requestCopy.validation.description;
      return '';

    case 'link':
      if (!value) return '';

      try {
        const parsedUrl = new URL(value);

        if (!['http:', 'https:'].includes(parsedUrl.protocol)) {
          return 'Ссылка должна начинаться с http:// или https://';
        }

        return '';
      } catch {
        return 'Введите корректную ссылку';
      }

    default:
      return '';
  }
};

export const ProjectRequestForm = ({
  idPrefix = 'project-request',
  onSubmitRequest,
  headingId,
  headingClassName,
  onConfirmed,
  onReset,
}) => {
  const formRef = useRef(null);
  const successHeadingRef = useRef(null);
  const submittingRef = useRef(false);
  const [values, setValues] = useState(initialValues);
  const [touched, setTouched] = useState({});
  const [formResetKey, setFormResetKey] = useState(0);
  const [submissionStatus, setSubmissionStatus] = useState('idle');

  useEffect(() => {
    if (submissionStatus !== 'success') return;

    const heading = successHeadingRef.current;
    heading?.focus({ preventScroll: true });

    const card = heading?.closest('section');
    if (!card || typeof card.scrollIntoView !== 'function') return;

    const cardTop = card.getBoundingClientRect().top;
    if (cardTop < 0 || cardTop > window.innerHeight) {
      card.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [submissionStatus]);

  const errors = useMemo(
    () =>
      Object.keys(initialValues).reduce((accumulator, field) => {
        accumulator[field] = validateField(field, values[field]);
        return accumulator;
      }, {}),
    [values]
  );

  const handleChange = field => event => {
    const nextValue = event.target.value;

    setValues(currentValues => ({
      ...currentValues,
      [field]: nextValue,
    }));

    setTouched(currentTouched => ({
      ...currentTouched,
      [field]: true,
    }));
  };

  const handleBlur = field => () => {
    setTouched(currentTouched => ({
      ...currentTouched,
      [field]: true,
    }));
  };

  const handleSubmit = async event => {
    event.preventDefault();
    if (submittingRef.current || submissionStatus === 'success') return;

    const allTouched = Object.keys(initialValues).reduce(
      (accumulator, field) => {
        accumulator[field] = true;
        return accumulator;
      },
      {}
    );

    setTouched(allTouched);

    const firstInvalidField = Object.keys(initialValues).find(field => errors[field]);
    if (firstInvalidField) {
      setSubmissionStatus('idle');
      const input = document.getElementById(`${idPrefix}-${firstInvalidField}`);
      input?.focus({ preventScroll: true });

      if (input && typeof input.scrollIntoView === 'function') {
        const rect = input.getBoundingClientRect();
        if (rect.top < 0 || rect.bottom > window.innerHeight) {
          input.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }
      return;
    }

    if (!onSubmitRequest) return;

    submittingRef.current = true;
    try {
      const submission = onSubmitRequest({ ...values });

      // A mailto draft has no server response, so it cannot confirm receipt.
      if (!submission || typeof submission.then !== 'function') {
        setSubmissionStatus('idle');
        return;
      }

      setSubmissionStatus('loading');
      const result = await submission;
      if (result?.confirmed === true) {
        onConfirmed?.();
        setSubmissionStatus('success');
      } else {
        setSubmissionStatus('error');
      }
    } catch {
      setSubmissionStatus('error');
    } finally {
      submittingRef.current = false;
    }
  };

  const handleSendAnother = () => {
    formRef.current?.reset();
    setValues(initialValues);
    setTouched({});
    setFormResetKey(currentKey => currentKey + 1);
    setSubmissionStatus('idle');
    onReset?.();
  };

  const getFieldProps = field => ({
    value: values[field],
    onChange: handleChange(field),
    onBlur: handleBlur(field),
    error: Boolean(touched[field] && errors[field]),
    helperText: touched[field] && errors[field] ? errors[field] : ' ',
  });

  const getInputAttrs = (field, attrs) => ({
    ...attrs,
    'aria-invalid': Boolean(touched[field] && errors[field]),
    'aria-describedby': `${idPrefix}-${field}-helper-text`,
  });

  if (submissionStatus === 'success') {
    return (
      <div className="project-request__success" aria-live="polite">
        <span className="project-request__success-icon" aria-hidden="true">
          <LuCheck />
        </span>
        <h2
          id={headingId}
          className={headingClassName || 'project-request__success-title'}
          ref={successHeadingRef}
          tabIndex={-1}
        >
          {requestCopy.successHeading}
        </h2>
        <p className="project-request__success-text">{requestCopy.successText}</p>

        <div className="project-request__next">
          <h3>{requestCopy.nextHeading}</h3>
          <ol>
            {requestCopy.nextSteps.map(step => <li key={step}>{step}</li>)}
          </ol>
        </div>

        <div className="project-request__add">
          <h3>{requestCopy.addHeading}</h3>
          <p>
            {requestCopy.addText}{' '}
            <a href={`mailto:${contactEmail}`}>{contactEmail}</a>
          </p>
        </div>

        <button
          type="button"
          className="btn btn--secondary project-request__reset-btn"
          onClick={handleSendAnother}
        >
          {requestCopy.sendAnother}
        </button>
      </div>
    );
  }

  return (
    <>
      {headingId && (
        <h2 id={headingId} className={headingClassName}>
          {requestCopy.formHeading}
        </h2>
      )}
      <p className="project-request__intro">
        {requestCopy.intro}
      </p>

      <form
        key={formResetKey}
        ref={formRef}
        className="project-request__form"
        onSubmit={handleSubmit}
        noValidate
      >
        <div className="project-request__grid">
          <div className="project-request__field-group">
            <label
              className="project-request__label"
              htmlFor={`${idPrefix}-name`}
            >
              {fieldLabels.name}
            </label>
            <TextField
              id={`${idPrefix}-name`}
              className="project-request__field"
              variant="outlined"
              fullWidth
              placeholder="Ваше имя"
              autoComplete="name"
              slotProps={{
                htmlInput: getInputAttrs('name', { maxLength: 120, name: 'name', required: true }),
              }}
              {...getFieldProps('name')}
            />
          </div>

          <div className="project-request__field-group">
            <label
              className="project-request__label"
              htmlFor={`${idPrefix}-email`}
            >
              {fieldLabels.email}
            </label>
            <TextField
              id={`${idPrefix}-email`}
              className="project-request__field"
              variant="outlined"
              fullWidth
              type="email"
              placeholder="name@company.com"
              autoComplete="email"
              slotProps={{
                htmlInput: getInputAttrs('email', { maxLength: 120, name: 'email', required: true }),
              }}
              {...getFieldProps('email')}
            />
          </div>
        </div>

        <div className="project-request__field-group">
          <label
            className="project-request__label"
            htmlFor={`${idPrefix}-company`}
          >
            {fieldLabels.company}
          </label>
          <TextField
            id={`${idPrefix}-company`}
            className="project-request__field"
            variant="outlined"
            fullWidth
            placeholder="Название компании"
            autoComplete="organization"
            slotProps={{
                htmlInput: getInputAttrs('company', { maxLength: 160, name: 'company' }),
            }}
            {...getFieldProps('company')}
          />
        </div>

        <div className="project-request__field-group">
          <label
            className="project-request__label"
            htmlFor={`${idPrefix}-service`}
            id={`${idPrefix}-service-label`}
          >
            {fieldLabels.service}
          </label>
          <Select
            id={`${idPrefix}-service`}
            className="project-request__service-select"
            labelId={`${idPrefix}-service-label`}
            variant="outlined"
            fullWidth
            required
            displayEmpty
            value={values.service}
            onChange={handleChange('service')}
            onBlur={handleBlur('service')}
            error={Boolean(touched.service && errors.service)}
            IconComponent={LuChevronDown}
            inputProps={{
              name: 'service',
              'aria-describedby': `${idPrefix}-service-error`,
              'aria-invalid': Boolean(touched.service && errors.service),
            }}
            renderValue={selected => selected
              ? serviceOptions.find(option => option.value === selected)?.label
              : <span className="project-request__service-placeholder">Выберите услугу</span>}
            MenuProps={{
              slotProps: {
                paper: { className: 'project-request__service-menu' },
              },
            }}
          >
            {serviceOptions.map(option => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </Select>
          <p
            id={`${idPrefix}-service-error`}
            className={`project-request__service-helper${touched.service && errors.service ? ' is-error' : ''}`}
            aria-live="polite"
          >
            {touched.service && errors.service ? errors.service : ' '}
          </p>
        </div>

        <div className="project-request__field-group">
          <label
            className="project-request__label"
            htmlFor={`${idPrefix}-description`}
          >
            {fieldLabels.description}
          </label>
          <TextField
            id={`${idPrefix}-description`}
            className="project-request__field"
            variant="outlined"
            fullWidth
            multiline
            minRows={4}
            placeholder="Расскажите о проекте, нужных результатах, примерном размере, уровне детализации и желаемых сроках."
            slotProps={{
              htmlInput: getInputAttrs('description', { maxLength: 1500, name: 'description', required: true }),
            }}
            {...getFieldProps('description')}
          />
        </div>

        <div className="project-request__field-group">
          <label
            className="project-request__label"
            htmlFor={`${idPrefix}-link`}
          >
            {fieldLabels.link}
          </label>
          <TextField
            id={`${idPrefix}-link`}
            className="project-request__field"
            variant="outlined"
            fullWidth
            type="url"
            placeholder="Ссылка на облачное хранилище"
            autoComplete="url"
            slotProps={{
              htmlInput: getInputAttrs('link', { maxLength: 500, name: 'link' }),
            }}
            {...getFieldProps('link')}
          />
        </div>

        <div className="project-request__actions">
          {submissionStatus === 'error' && (
            <div className="project-request__error" role="alert">
              <strong>{requestCopy.errorHeading}</strong>
              <p>
                {requestCopy.errorText}{' '}
                <a href={`mailto:${contactEmail}`}>{contactEmail}</a>.
              </p>
            </div>
          )}
          <button
            type="submit"
            className="btn project-request__btn"
            disabled={submissionStatus === 'loading'}
            aria-live="polite"
          >
            {submissionStatus === 'loading' && (
              <span className="project-request__spinner" aria-hidden="true" />
            )}
            {submissionStatus === 'loading'
              ? requestCopy.loading
              : submissionStatus === 'error' ? requestCopy.retry : requestCopy.submit}
          </button>
          <p className="project-request__note">
            {requestCopy.note}
          </p>
        </div>
      </form>
    </>
  );
};
